import { Injectable } from "@angular/core";
import { CachedValue } from "src/gyzmo-commons/models/cachedValue.model";
import { DateHelper } from "../../helpers/date.helper";
import { DateProvider } from "../../interfaces/dateProvider";
import { AppSqlProvider } from "../../persistence/app.sql.provider";
import { LoggerService } from "../../services/logs/logger.service";
import { DbDaoBase } from "./base/db.dao.base";

@Injectable({
    providedIn: "root",
})
export class CacheDbDao extends DbDaoBase<CachedValue> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private dateProvider: DateProvider,
    ) {
        super(logger);
    }

    public getCachedKeys(): Promise<string[]> {
        const query = `SELECT key FROM ${ CachedValue.TABLENAME };`;
        return this.sqlProvider
            .query(query, [])
            .then((data) => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const keys: string[] = [];
                for (const item of data.rows) {
                    const key = item.key;
                    keys.push(key);
                }
                return keys;
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return null;
            });
    }

    public isCached(key: string): Promise<boolean> {
        const selectQuery = `SELECT key FROM ${ CachedValue.TABLENAME } WHERE key = ? AND expirationDate >= ?;`;
        const params = [key, this.dateProvider.now().toISO()];
        return this.sqlProvider
            .query(selectQuery, params)
            .then((data) => {
                return data.rows.length > 0;
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return false;
            });
    }

    public cleanCache() {
        const selectQuery = `DELETE FROM ${ CachedValue.TABLENAME } WHERE expirationDate < ?;`;
        const params = [this.dateProvider.now().toISO()];
        return this.sqlProvider.query(selectQuery, params);
    }

    public async createIndexes(): Promise<void> {
        const query = `CREATE INDEX IF NOT EXISTS idx_${ CachedValue.TABLENAME }_expirationDate ON ${ CachedValue.TABLENAME }(expirationDate);`;
        await this.sqlProvider.query(query, []).catch((reason) => {
            this.logSqlError(reason);
        });
    }

    public createTable(): Promise<void> {
        const query = `
            CREATE TABLE IF NOT EXISTS ${ CachedValue.TABLENAME } (
                key TEXT PRIMARY KEY,
                value TEXT,
                date TEXT,
                expirationDate TEXT
            );
        `;
        return this.sqlProvider
            .query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(key: string): Promise<any> {
        const selectQuery = `DELETE FROM ${ CachedValue.TABLENAME } WHERE key = ?;`;
        const params = [key];
        return this.sqlProvider.query(selectQuery, params);
    }

    deleteAll(): Promise<any> {
        const selectQuery = `DELETE FROM ${ CachedValue.TABLENAME };`;
        return this.sqlProvider.query(selectQuery, []);
    }

    public get(key: string, hydrate: boolean = false): Promise<CachedValue> {
        const selectQuery = `SELECT * FROM ${ CachedValue.TABLENAME } WHERE key = ? AND expirationDate >= ?;`;
        const params = [key, this.dateProvider.now().toISO()];
        return this.sqlProvider
            .query(selectQuery, params)
            .then((data) => {
                if (data.rows.length <= 0) {
                    return null;
                }
                return CachedValue.fromRow(data.rows[0]);
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return CachedValue.TABLENAME;
    }

    protected rowToModel(row: any): CachedValue {
        return CachedValue.fromRow(row);
    }

    public save(cachedValue: CachedValue): Promise<CachedValue> {
        const query = `
            INSERT OR REPLACE INTO ${ CachedValue.TABLENAME } (key, value, date, expirationDate)
            VALUES (?, ?, ?, ?);
        `;
        const params = [
            cachedValue.key,
            cachedValue.value,
            DateHelper.tryToISO(cachedValue.date),
            DateHelper.tryToISO(cachedValue.expirationDate),
        ];
        return this.sqlProvider
            .query(query, params)
            .then(() => {
                return cachedValue;
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return null;
            });
    }

    public dropTable(): Promise<void> {
        const query = `DROP TABLE IF EXISTS ${ CachedValue.TABLENAME };`;
        return this.sqlProvider
            .query(query, [])
            .then(() => {
                return;
            })
            .catch((reason) => {
                this.logSqlError(reason);
                return null;
            });
    }
}
